/**
 * What the Live activity page calls each operation, background job and System Controls switch.
 *
 * Operations are keyed on dbo.ActionLogTypes.Name ("Controller.Action", written by ActionLogFilter; AuthRejected,
 * Forbidden and NotFound by ActionLogMiddleware). Each has the route it answers, so a sign-in rejection, which has
 * only a method and path, gets the same name. `*` in a route stands for one path segment (an id or a key).
 * Anything not listed falls back to a readable form of its name or route, never a blank.
 */

export interface OperationLabel {
  label: string;
  /** "METHOD /api/path", `*` for one variable segment. */
  route?: string;
}

export const OPERATION_LABELS: Readonly<Record<string, OperationLabel>> = {
  // Refused before reaching an operation (ActionLogMiddleware).
  'AuthRejected': { label: 'Refused: sign-in not accepted' },
  'Forbidden': { label: 'Refused: no permission' },
  'NotFound': { label: 'Unknown address' },

  // Signing in and the user's own account.
  'User.Authenticate': { label: 'Sign in', route: 'POST /api/user/authenticate' },
  'User.AuthenticateAdUser': { label: 'Sign in with a SEG account', route: 'POST /api/user/authenticateAdUser' },
  'User.Refresh': { label: 'Renew sign-in', route: 'POST /api/user/refresh' },
  'User.MyFeatures': { label: 'Load my permissions', route: 'GET /api/user/MyFeatures' },
  'User.MyBrands': { label: 'Load my companies', route: 'GET /api/User/MyBrands' },
  'User.Register': { label: 'Register an account', route: 'POST /api/user/register' },
  'User.ResetPassword': { label: 'Reset password', route: 'POST /api/user/resetpassword' },
  'User.SendPassworResetLink': { label: 'Send password reset link', route: 'POST /api/user/SendPassworResetLink' },
  'User.GetUserNameByAuthenticationId': { label: 'Look up user name', route: 'GET /api/user/GetUserName' },
  'User.GetUserByRole': { label: 'List users by role', route: 'GET /api/user/GetUserByRole' },
  'User.AddNewUserClient': { label: 'Link a user to a client', route: 'POST /api/user/AddNewUserClient' },
  'User.CreateClientUserAccount': { label: 'Create client user account', route: 'POST /api/user/CreateClientUserAccount' },
  'User.EnsureTutorAccounts': { label: 'Create missing tutor accounts', route: 'POST /api/User/EnsureTutorAccounts' },
  'User.ValidAccess': { label: 'Learner signs in', route: 'POST /api/user/ValidateStudentAccess' },
  'FeatureSwitches.Enabled': { label: 'Check which features are on', route: 'GET /api/FeatureSwitches/Enabled' },
  'Settings.Public': { label: 'Load settings', route: 'GET /api/Settings/Public' },
  'Config.ReadThemeSiteContent': { label: 'Load page content', route: 'POST /api/config/ReadThemeSiteContent' },
  'TutorReport.GetDateTimeNow': { label: 'Check server time', route: 'GET /api/TutorReport/GetDateTimeNow' },

  // Sessions and session reports.
  'Session.GetSessions': { label: 'List sessions', route: 'POST /api/session/GetSessionsV2' },
  'Session.SessionTileRows': { label: 'Load sessions tile', route: 'POST /api/session/SessionTileRows' },
  'Session.GetPlacements': { label: 'List bookings', route: 'POST /api/session/GetPlacements' },
  'Session.GetFullSessionByPlacement': { label: "View a booking's sessions", route: 'POST /api/session/GetFullSessionByPlacement' },
  'Session.GetSessionReport': { label: 'Open session report', route: 'GET /api/Session/SessionReport/*/*' },
  'Session.SaveSessionReportSession': { label: 'Submit session register', route: 'POST /api/Session/SessionReport/Session' },
  'Session.SaveSessionReportReport': { label: 'Save or submit session report', route: 'POST /api/Session/SessionReport/Report' },
  'Session.SaveSessionReportBehaviouralConcern': { label: 'Raise a behavioural incident', route: 'POST /api/Session/SessionReport/BehaviouralConcern' },
  'Session.SaveSessionReportTutorCancellation': { label: 'Record a tutor cancellation', route: 'POST /api/Session/SessionReport/TutorCancellation' },
  'Session.SaveSessionReportConsultantNotes': { label: 'Save consultant notes on a register', route: 'POST /api/Session/SessionReport/ConsultantNotes' },
  'Session.RejectSessionReport': { label: 'Reject session report', route: 'POST /api/Session/SessionReport/Reject' },
  'Session.ReopenSessionReport': { label: 'Reopen session report', route: 'POST /api/Session/SessionReport/Reopen' },
  'Session.UnlockSessionReportAttendance': { label: 'Unlock session register', route: 'POST /api/Session/SessionReport/UnlockAttendance' },
  'Session.GetQaConsultants': { label: 'Load QA consultants', route: 'GET /api/Session/Qa/consultants' },
  'Session.SearchQaReports': { label: 'Search reports awaiting QA', route: 'POST /api/Session/Qa/reports/search' },

  // Bookings.
  'TutorReport.RunPlacementSearch': { label: 'Search bookings', route: 'POST /api/TutorReport/RunPlacementSearch' },
  'TutorReport.GetPlacementById': { label: 'View booking', route: 'GET /api/TutorReport/GetPlacementById/*' },
  'TutorReport.PlacementMaintenance': { label: 'Booking maintenance', route: 'POST /api/TutorReport/PlacementMaintenance' },
  'TutorReport.GetIndividualLearningPlanFrame': { label: 'Load learning plan outline', route: 'POST /api/TutorReport/GetIndividualLearningPlanFrame' },
  'TutorReport.GetUserAccounts': { label: 'Load user accounts', route: 'GET /api/TutorReport/GetUserAccounts' },
  'TutorReport.MissingSubmissionsFast': { label: 'List missing submissions', route: 'POST /api/TutorReport/MissingSubmissionsFast' },
  'TutorReport.GetMissingSubmissionsSearch': { label: 'Search missing submissions', route: 'POST /api/TutorReport/getMissingSubmissionsSearch' },
  'TutorReport.UnlockILP': { label: 'Unlock learning plan', route: 'POST /api/TutorReport/UnlockILP' },
  'TutorReport.UnlockProgressReport': { label: 'Unlock progress report', route: 'POST /api/TutorReport/UnlockProgressReport' },
  'TutorReport.LaunchDigitalClassroom': { label: 'Tutor joins Digital Classroom', route: 'POST /api/TutorReport/LaunchDigitalClassroom' },
  'TutorReport.LaunchOnlineOverview': { label: 'Open online overview', route: 'POST /api/TutorReport/LaunchOnlineOverview' },
  'TutorReport.LaunchVideoLibrary': { label: 'Open video library', route: 'POST /api/TutorReport/LaunchVideoLibrary' },

  // Learning plans, progress reports, milestones, risk assessments.
  'Ilp.Get': { label: 'View learning plan', route: 'GET /api/Ilp/*/*' },
  'Ilp.Save': { label: 'Save learning plan', route: 'POST /api/Ilp/save' },
  'Ilp.Submit': { label: 'Submit learning plan', route: 'POST /api/Ilp/submit' },
  'Ilp.Authorise': { label: 'Approve learning plan', route: 'POST /api/Ilp/authorise' },
  'Ilp.Reject': { label: 'Reject learning plan', route: 'POST /api/Ilp/reject' },
  'Ilp.Unlock': { label: 'Unlock learning plan', route: 'POST /api/Ilp/unlock' },
  'ProgressReport.Get': { label: 'View progress report', route: 'GET /api/ProgressReport/*' },
  'ProgressReport.List': { label: 'List progress reports', route: 'POST /api/ProgressReport/list' },
  'ProgressReport.Save': { label: 'Save progress report', route: 'POST /api/ProgressReport/save' },
  'ProgressReport.Submit': { label: 'Submit progress report', route: 'POST /api/ProgressReport/submit' },
  'ProgressReport.Authorise': { label: 'Approve progress report', route: 'POST /api/ProgressReport/authorise' },
  'ProgressReport.Unlock': { label: 'Unlock progress report', route: 'POST /api/ProgressReport/unlock' },
  'Milestone.List': { label: 'View milestones', route: 'POST /api/Milestone/list' },
  'Milestone.Save': { label: 'Save milestone', route: 'POST /api/Milestone/save' },
  'RiskAssessment.Get': { label: 'View risk assessment', route: 'POST /api/RiskAssessment/get' },
  'RiskAssessment.Lookup': { label: 'Load risk assessment options', route: 'GET /api/RiskAssessment/lookup' },
  'RiskAssessment.Submit': { label: 'Submit risk assessment', route: 'POST /api/RiskAssessment/submit' },
  'LearningSection.RetrieveStudentTimeLine': { label: 'View learner timeline', route: 'POST /api/LearningSection/RetrieveStudentTimeLine' },

  // Learners.
  'Students.StudentSelect': { label: 'Select a learner', route: 'POST /api/Students/StudentSelect' },
  'Students.GenerateAccess': { label: 'Create learner access', route: 'POST /api/Students/GenerateAccess' },
  'Students.ReadPlacementInformation': { label: 'Learner views their booking', route: 'POST /api/Students/ReadPlacementInformation' },
  'Students.ReadUpcomingSessions': { label: 'Learner views upcoming sessions', route: 'POST /api/Students/ReadUpcomingSessions' },
  'Students.LaunchDigitalClassroom': { label: 'Learner joins Digital Classroom', route: 'POST /api/Students/LaunchDigitalClassroom' },

  // Feedback.
  'Feedback.CheckUserFeedbackSurveyOnLogin': { label: 'Check for a feedback survey', route: 'POST /api/Feedback/CheckUserFeedbackSurveyOnLogin' },
  'Feedback.GenerateStudentTuitionFeedbackLink': { label: 'Create learner feedback link', route: 'POST /api/Feedback/GenerateStudentTuitionFeedbackLink' },
  'Feedback.RequestTuitionFeedbackSurveyGuid': { label: 'Open tuition feedback survey', route: 'POST /api/Feedback/RequestTuitionFeedbackSurveyGuid' },
  'Feedback.SaveFeedback': { label: 'Save feedback', route: 'POST /api/Feedback/SaveFeedback' },
  'Feedback.SearchFeedbackDataDashboard': { label: 'Search feedback dashboard', route: 'POST /api/Feedback/SearchFeedbackDataDashboard' },

  // Clients and reports.
  'Clients.GetClients': { label: 'Load clients', route: 'GET /api/Clients/GetClients' },
  'Clients.ClientListForAddStudent': { label: 'Load clients for a new learner', route: 'GET /api/Clients/GetClientListForAddStudent' },
  'Clients.GetClientsForClassroomOverview': { label: 'Load clients for classroom overview', route: 'GET /api/Clients/GetClientsForClassroomOverview' },
  'Clients.GetClientsForReports': { label: 'Load clients for reports', route: 'GET /api/Clients/GetClientsForReports' },
  'Clients.ClientUserSearch': { label: 'Search client users', route: 'POST /api/Clients/ClientUserSearch' },
  'Clients.GetClientUser': { label: 'View client user', route: 'GET /api/Clients/GetClientUser' },
  'Clients.CreateClientUsers': { label: 'Create client users', route: 'POST /api/Clients/CreateClientUsers' },
  'Roles.GetClientRoles': { label: 'Load client roles', route: 'GET /api/Roles/GetClientRoles' },
  'Roles.UpdateRole': { label: 'Change a client role', route: 'POST /api/Roles/UpdateRole' },
  'ClientReport.Brands': { label: 'Load client report options', route: 'GET /api/ClientReport/brands' },
  'ClientReport.RunAttendance': { label: 'Run attendance report', route: 'POST /api/ClientReport/attendance/run' },
  'ClientReport.RunMilpr': { label: 'Run MILPR report', route: 'POST /api/ClientReport/milpr/run' },
  'Reports.GetReports': { label: 'Load reports', route: 'GET /api/reports/GetReports' },
  'Reports.GetTutorsForReport': { label: 'Load tutors for a report', route: 'POST /api/reports/GetTutorsForReport' },
  'Reports.GetStudentsForReport': { label: 'Load learners for a report', route: 'POST /api/reports/GetStudentsForReport' },
  'Reports.GetCoordinatorsForReport': { label: 'Load coordinators for a report', route: 'POST /api/reports/GetCoordinatorsForReport' },
  'Reports.GetReferrersForReport': { label: 'Load referrers for a report', route: 'POST /api/reports/GetReferrersForReport' },
  'Actions.GetActions': { label: 'View actions', route: 'GET /api/actions/GetActions' },
  'Actions.GetActionsSearch': { label: 'Search actions', route: 'POST /api/actions/getActionsSearch' },
  'Actions.GetAllActionTypes': { label: 'Load action types', route: 'GET /api/actions/GetAllActionTypes' },

  // Tutor nudger (the poller).
  'Workflow.Schedule': { label: 'Nudger: read schedule', route: 'POST /api/Workflow/Schedule' },
  'Workflow.GenerateWorkFlowActions': { label: 'Nudger: create actions', route: 'POST /api/Workflow/GenerateWorkFlowActions' },
  'Workflow.GenerateDailyWorkFlowActions': { label: 'Nudger: create daily actions', route: 'POST /api/Workflow/GenerateDailyWorkFlowActions' },
  'Workflow.GetWorkFlowActions': { label: 'Nudger: read actions', route: 'POST /api/Workflow/GetWorkFlowActions' },
  'Workflow.SendTutorNudgerEmail': { label: 'Nudger: email a tutor', route: 'POST /api/Workflow/SendTutorNudgerEmail' },
  'Workflow.SendTutorNudgerSms': { label: 'Nudger: text a tutor', route: 'POST /api/Workflow/SendTutorNudgerSms' },

  // Admin screens.
  'Maintenance.SearchUsers': { label: 'Search users', route: 'POST /api/Maintenance/Users/Search' },
  'Maintenance.GetUser': { label: 'View user account', route: 'GET /api/Maintenance/Users/*' },
  'Maintenance.AssignRole': { label: 'Assign a role', route: 'POST /api/Maintenance/Users/Roles/Assign' },
  'Maintenance.SetActive': { label: 'Activate or deactivate a user', route: 'POST /api/Maintenance/Users/SetActive' },
  'Maintenance.SendPasswordReset': { label: 'Send a password reset', route: 'POST /api/Maintenance/Users/SendPasswordReset' },
  'Maintenance.GetRoles': { label: 'Load roles', route: 'GET /api/Maintenance/Roles' },
  'Maintenance.GetRoleFeatures': { label: 'Load role features', route: 'POST /api/Maintenance/Roles/Features' },
  'Maintenance.SetRoleFeature': { label: 'Change a role feature', route: 'POST /api/Maintenance/Roles/Features/Set' },
  'Maintenance.GetFeatureSwitches': { label: 'Load new features', route: 'GET /api/Maintenance/FeatureSwitches' },
  'Maintenance.SetFeatureSwitch': { label: 'Change a new feature switch', route: 'PUT /api/Maintenance/FeatureSwitches/*' },
  'Maintenance.GetServiceProcesses': { label: 'Load System Controls', route: 'GET /api/Maintenance/Processes' },
  'Maintenance.SetServiceProcess': { label: 'Change a System Control', route: 'POST /api/Maintenance/Processes/Set' },
  'StaffSyncAdmin.GetSettings': { label: 'Load System Controls settings', route: 'GET /api/Maintenance/Settings' },
  'StaffSyncAdmin.GetStaffSync': { label: 'View staff sync report', route: 'GET /api/Maintenance/StaffSync' },
  'StaffSyncAdmin.GetOrbitRoleMap': { label: 'Load Orbit role map', route: 'GET /api/Maintenance/OrbitRoleMap' },
  'StaffSyncAdmin.GetLeapStaffRoles': { label: 'Load staff roles', route: 'GET /api/Maintenance/LeapStaffRoles' },
  'EmailTemplates.GetCatalogue': { label: 'Load email templates', route: 'GET /api/EmailTemplates' },
  'EmailTemplates.GetWording': { label: 'View email wording', route: 'GET /api/EmailTemplates/*/Wording' },
  'EmailTemplates.Preview': { label: 'Preview an email', route: 'GET /api/EmailTemplates/*/Preview' },
  'EmailTemplates.PreviewWording': { label: 'Preview edited email wording', route: 'POST /api/EmailTemplates/*/Wording/Preview' },
  'EmailTemplates.SendTest': { label: 'Send a test email', route: 'POST /api/EmailTemplates/*/Test' },
  'EmailHistory.Search': { label: 'Search email history', route: 'GET /api/EmailHistory' },
  'EmailHistory.Summary': { label: 'Email history summary', route: 'GET /api/EmailHistory/Summary' },
  'LiveActivity.Summary': { label: 'Live activity summary', route: 'GET /api/LiveActivity/Summary' },
  'LiveActivity.UserRecent': { label: "View a user's recent actions", route: 'GET /api/LiveActivity/Users/*/Recent' }
};

/** Background job rows (ActionLogs with no user and no HTTP method). */
export const JOB_LABELS: Readonly<Record<string, { label: string; description: string }>> = {
  'ClientSync': { label: 'Client sync from Orbit', description: 'Copies new and changed clients from Orbit, every few minutes.' },
  'PlacementSync': { label: 'Booking sync from Orbit', description: 'Copies bookings and their learners from Orbit, straight after the client sync.' },
  'DigitalClassroomSweep': { label: 'Digital Classroom set-up', description: 'Creates a classroom for each online booking that has none yet.' },
  'StaffSync': { label: 'Staff sync from Orbit', description: 'Updates staff accounts and their roles from Orbit.' },
  'SessionRejection.Email': { label: 'Session report rejection email', description: 'Tells the tutor a session report was rejected.' },
  'ReportRejection.Email': { label: 'Report rejection email', description: 'Tells the tutor a learning plan or progress report was rejected.' }
};

/** System Controls switches (ServiceProcessKeys), as they appear in "stopped by a switch". */
export const PROCESS_LABELS: Readonly<Record<string, string>> = {
  'email.outbound': 'All email',
  'sms.outbound': 'All text messages',
  'nudger.workflow.generate': 'Nudger: create actions',
  'nudger.workflow.email': 'Nudger emails',
  'nudger.workflow.sms': 'Nudger texts',
  'nudger.weekly.email': 'Nudger weekly email',
  'tutoraccounts.ensure': 'Create missing tutor accounts',
  'tutoraccounts.summary.email': 'New tutor accounts summary email',
  'identity.provision': 'Sign-in account set-up',
  'user.registration.email': 'Registration emails',
  'clientreports.email': 'Client report emails',
  'learnerjourney.absence.email': 'Learner absence emails',
  'session.rejection.email': 'Session rejection emails',
  'session.behaviour-incident.email': 'Behavioural incident emails',
  'report.rejection.email': 'Report rejection emails',
  'emailtemplates.test': 'Email template test sends',
  'user.password-reset.email': 'Password reset emails',
  'learner.feedback.email': 'Learner feedback emails',
  'reports.batch.email': 'Report batch emails',
  'learner.signup.email': 'Learner sign-up emails',
  'learner.weblink.email': 'Learner web link emails',
  'clientupload.notification.email': 'Client upload notification emails',
  'communication.sendmail.email': 'Other emails',
  'account.security.email': 'Account security emails',
  'auth.mfa.sms': 'Sign-in codes by text',
  'communication.sendsms.sms': 'Other text messages',
  'sync.clients': 'Client sync from Orbit',
  'sync.digitalclassroom': 'Digital Classroom set-up'
};

/** "GetPlacementById" -> "get placement by id"; "UnlockILP" -> "unlock ILP". */
function words(value: string): string {
  return value
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_.]+/g, ' ')
    .split(/\s+/)
    .filter(w => w)
    .map(w => (w.length > 1 && w === w.toUpperCase()) ? w : w.toLowerCase())
    .join(' ');
}

function capitalise(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/** A segment that is an id rather than a name: a number, a GUID or a long hex string. */
function isIdSegment(segment: string): boolean {
  return /^\d+$/.test(segment) || /^[0-9a-f-]{16,}$/i.test(segment);
}

/** "TutorReport.GetPlacementById" -> "Tutor report: get placement by id". */
export function readableName(name: string | null | undefined): string {
  const value = (name ?? '').trim();
  if (!value) {
    return 'Unknown operation';
  }
  const dot = value.indexOf('.');
  if (dot < 0) {
    return capitalise(words(value));
  }
  return `${capitalise(words(value.slice(0, dot)))}: ${words(value.slice(dot + 1))}`;
}

/** "GET /api/Foo/BarBaz/12" -> "Foo: bar baz". */
export function readablePath(path: string | null | undefined): string {
  const segments = (path ?? '').split('?')[0].split('/').filter(s => s && s.toLowerCase() !== 'api' && !isIdSegment(s));
  if (segments.length === 0) {
    return 'Unknown request';
  }
  const [first, ...rest] = segments.map(words);
  return rest.length ? `${capitalise(first)}: ${rest.join(' ')}` : capitalise(first);
}

/** The on-screen name of an operation (an ActionLogTypes name). */
export function operationLabel(actionType: string | null | undefined): string {
  return OPERATION_LABELS[(actionType ?? '').trim()]?.label ?? readableName(actionType);
}

function routeMatches(route: string, method: string, path: string): boolean {
  const [routeMethod, routePath] = route.split(' ');
  if (routeMethod.toUpperCase() !== method.toUpperCase()) {
    return false;
  }
  const want = routePath.toLowerCase().split('/').filter(s => s);
  const have = path.split('?')[0].toLowerCase().split('/').filter(s => s);
  return want.length === have.length && want.every((s, i) => s === '*' || s === have[i]);
}

/** The on-screen name of a request known only by its method and path (a sign-in rejection). */
export function requestLabel(method: string | null | undefined, path: string | null | undefined): string {
  if (method && path) {
    for (const entry of Object.values(OPERATION_LABELS)) {
      if (entry.route && routeMatches(entry.route, method, path)) {
        return entry.label;
      }
    }
  }
  return readablePath(path);
}

export function jobLabel(name: string | null | undefined): string {
  return JOB_LABELS[(name ?? '').trim()]?.label ?? readableName(name);
}

export function jobDescription(name: string | null | undefined): string {
  return JOB_LABELS[(name ?? '').trim()]?.description ?? '';
}

/** The on-screen name of a System Controls switch; an unknown key reads as words. */
export function processLabel(key: string | null | undefined): string {
  const value = (key ?? '').trim();
  return PROCESS_LABELS[value] ?? (value ? capitalise(words(value)) : 'Unknown switch');
}

/**
 * An HTTP status in words: "OK", "Refused (no permission)", "Failed (server error)". With no status, the
 * success flag decides.
 */
export function statusLabel(statusCode: number | null | undefined, success?: boolean | null): string {
  if (statusCode == null) {
    return success === false ? 'Failed' : 'OK';
  }
  if (statusCode >= 500) {
    return 'Failed (server error)';
  }
  switch (statusCode) {
    case 304: return 'OK (unchanged)';
    case 400: return 'Refused (request not valid)';
    case 401: return 'Refused (not signed in)';
    case 403: return 'Refused (no permission)';
    case 404: return 'Not found';
    case 409: return 'Refused (conflict)';
    case 422: return 'Refused (could not be accepted)';
    case 429: return 'Refused (too many requests)';
  }
  if (statusCode >= 200 && statusCode < 400) {
    return success === false ? 'Failed' : 'OK';
  }
  return `Refused (${statusCode})`;
}

/** "350 ms", "2.4 s", "1 min 5 s"; "-" when not timed. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) {
    return '-';
  }
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  if (ms < 60000) {
    const s = Math.round(ms / 100) / 10;
    return `${s % 1 === 0 ? s.toFixed(0) : s.toFixed(1)} s`;
  }
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
}
